import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { PhoneCall, ArrowRight } from 'lucide-react';
import { Badge } from './ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEPT_LABEL = { service: 'Service', new_construction: 'New Construction' };

/** iter194 — Build initials from a name (Adam Cordle → "AC"). Used as an avatar
 *  fallback until we wire up profile images. */
const initialsOf = (name) => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?';
};

/** iter194 — Format a period as "Sat 2/7 – Fri 2/13". Times are stored as
 *  YYYY-MM-DD from the rotation engine; parse locally without introducing UTC
 *  drift by pinning the "T00:00:00" segment. */
const fmtPeriod = (start, end) => {
  if (!start || !end) return '';
  try {
    const s = new Date(`${start}T00:00:00`);
    const e = new Date(`${end}T00:00:00`);
    const dow = (d) => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
    const md = (d) => `${d.getMonth() + 1}/${d.getDate()}`;
    return `${dow(s)} ${md(s)} – ${dow(e)} ${md(e)}`;
  } catch {
    return `${start} → ${end}`;
  }
};

/** Compact card showing who's currently on-call by department.
 *  iter194 — Also shows "On Call Next Week" underneath so techs can plan ahead.
 *  Falls back to "Not Assigned" when the rotation is unconfigured for a dept. */
export const OnCallBanner = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/on-call/today`, { withCredentials: true })
      .then((r) => setData(r.data || {}))
      .catch(() => setData({}));
  }, []);
  if (!data || Object.keys(data).length === 0) return null;
  const depts = Object.entries(data);
  return (
    <div data-testid="on-call-banner" className="bg-[#0f0f0f] border border-[#D4AF37]/30 rounded-md p-4 space-y-3">
      <div className="flex items-center gap-2">
        <PhoneCall className="w-4 h-4 text-[#D4AF37]" />
        <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold">Currently On Call</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {depts.map(([dept, info]) => (
          <div
            key={dept}
            data-testid={`on-call-banner-${dept}`}
            className="bg-[#141414] border border-[#262626] rounded-md p-3 space-y-3"
          >
            <div className="flex items-center gap-2">
              <Badge className="bg-[#1f1f1f] border-[#333] text-[#A3A3A3] text-[10px] uppercase">{DEPT_LABEL[dept] || dept}</Badge>
              {info.source === 'override' && (
                <Badge className="bg-amber-500/15 text-amber-400 border-amber-700 text-[9px] uppercase">Override</Badge>
              )}
            </div>

            {/* Current — visually dominant */}
            <div className="flex items-center gap-3" data-testid={`on-call-current-${dept}`}>
              <div className="w-11 h-11 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/60 flex items-center justify-center text-[#D4AF37] font-bold">
                {initialsOf(info.user_name)}
              </div>
              <div className="min-w-0">
                <p className="text-white text-base font-bold truncate" data-testid={`on-call-current-name-${dept}`}>
                  {info.user_name || 'Not Assigned'}
                </p>
                {info.period_start && info.period_end && (
                  <p className="text-[#A3A3A3] text-xs" data-testid={`on-call-current-range-${dept}`}>
                    {fmtPeriod(info.period_start, info.period_end)}
                  </p>
                )}
              </div>
            </div>

            {/* Next — smaller, secondary */}
            <div className="border-t border-[#262626] pt-2">
              <p className="text-[10px] uppercase tracking-wider text-[#A3A3A3] font-bold flex items-center gap-1 mb-1.5">
                <ArrowRight className="w-3 h-3" /> On Call Next Week
              </p>
              {info.next && info.next.user_name ? (
                <div className="flex items-center gap-2" data-testid={`on-call-next-${dept}`}>
                  <div className="w-7 h-7 rounded-full bg-[#262626] border border-[#404040] flex items-center justify-center text-[#C9C9C9] text-[11px] font-bold">
                    {initialsOf(info.next.user_name)}
                  </div>
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate" data-testid={`on-call-next-name-${dept}`}>
                      {info.next.user_name}
                    </p>
                    <p className="text-[#525252] text-[11px]" data-testid={`on-call-next-range-${dept}`}>
                      {fmtPeriod(info.next.period_start, info.next.period_end)}
                    </p>
                  </div>
                  {info.next.source === 'override' && (
                    <Badge className="bg-amber-500/15 text-amber-400 border-amber-700 text-[9px] uppercase ml-auto">Override</Badge>
                  )}
                </div>
              ) : (
                <p className="text-[#525252] text-sm italic" data-testid={`on-call-next-empty-${dept}`}>Not Assigned</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OnCallBanner;
